import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { superAdminAPI, rbacAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { ArrowLeft, Loader2, Save, Building2, ShieldAlert, ShieldCheck, Users, DollarSign, UserCheck, UserMinus, Trash2, AlertTriangle, Key, Download, Plus, User, Edit, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { ConfirmationDialog, useConfirmation } from '../../components/ui/confirmation-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { formatIndianCurrency } from '../../lib/utils';
import { Checkbox } from '../../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dialogProps, confirm } = useConfirmation();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [purgePasscode, setPurgePasscode] = useState('');
  const [purging, setPurging] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportConsent, setExportConsent] = useState(false);

  // User Management State
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_id: '',
    is_system: true
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let pass = "";
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setUserForm(prev => ({ ...prev, password: pass }));
    setShowPassword(true);
    toast.info("Password generated");
  };

  const [modules, setModules] = useState({
    employees: true,
    onboarding: true,
    leave: true,
    payroll: true,
    salary_structure: true,
    salary_advances: true,
    performance: true,
    tax: true,
    attendance: true,
    feeds: true,
    org_chart: true,
    stationery: false,
    letters_reports: false,
  });

  useEffect(() => {
    fetchCompany();
    fetchUsers();
  }, [id]);

  const fetchUsers = async () => {
    try {
      const [uRes, rRes] = await Promise.all([
        rbacAPI.getUsers(id),
        rbacAPI.getRoles(id)
      ]);
      setUsers(uRes.data);
      setRoles(rRes.data);
    } catch (error) {
      console.error("Failed to load users", error);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserForm({ first_name: '', last_name: '', email: '', password: '', role_id: '', is_system: true });
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setUserForm({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      password: '',
      role_id: u.role_id,
      is_system: u.is_system
    });
    setShowPassword(false);
    setIsUserDialogOpen(true);
  };

  const handleDeleteUser = async (u) => {
    const confirmed = await confirm({
      title: 'Delete User',
      description: `Are you sure you want to delete ${u.first_name} ${u.last_name}?`,
      confirmText: 'Delete User',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await rbacAPI.deleteUser(u.id);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      if (editingUser) {
        const payload = { ...userForm };
        if (!payload.password) delete payload.password;
        await rbacAPI.updateUser(editingUser.id, payload);
        toast.success("User updated successfully");
      } else {
        await rbacAPI.createUser({
          ...userForm,
          company_id: id
        });
        toast.success("User created successfully");
      }
      setIsUserDialogOpen(false);
      fetchUsers();
      setUserForm({ first_name: '', last_name: '', email: '', password: '', role_id: '', is_system: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save user");
    } finally {
      setCreatingUser(false);
    }
  };

  const fetchCompany = async () => {
    try {
      const response = await superAdminAPI.getCompany(id);
      setCompany(response.data);
      setFormData({
        ...response.data,
        license_start_date: response.data.license_start_date ? new Date(response.data.license_start_date).toISOString().split('T')[0] : ''
      });
      setModules(response.data.modules || {
        employees: true,
        onboarding: true,
        leave: true,
        payroll: true,
        salary_structure: true,
        salary_advances: true,
        performance: true,
        tax: true,
        attendance: true,
        feeds: true,
        org_chart: true,
        stationery: false,
        letters_reports: false,
      });

      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to fetch company details');
      navigate('/superadmin/companies');
    } finally {
      setLoading(false);
    }
  };
  const fetchAnalytics = async (month = selectedMonth, year = selectedYear) => {
    try {
      const response = await superAdminAPI.getCompanyAnalytics(id, { month, year });
      setAnalytics(response.data);
      if (response.data.month) setSelectedMonth(response.data.month);
      if (response.data.year) setSelectedYear(response.data.year);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  const handleMonthChange = (val) => {
    const month = parseInt(val);
    setSelectedMonth(month);
    fetchAnalytics(month, selectedYear);
  };

  const handleYearChange = (val) => {
    const year = parseInt(val);
    setSelectedYear(year);
    fetchAnalytics(selectedMonth, year);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await superAdminAPI.updateCompany(id, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        pf_number: formData.pf_number,
        esi_number: formData.esi_number,
        tan: formData.tan,
        pan: formData.pan,
        pt_state: formData.pt_state,
        license_start_date: formData.license_start_date,
        license_duration_months: formData.license_duration_months,
      });
      toast.success('Company updated successfully');
      fetchCompany();
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleModuleToggle = async (module, enabled) => {
    const newModules = { ...modules, [module]: enabled };
    setModules(newModules);

    try {
      await superAdminAPI.updateCompanyModules(id, newModules);
      toast.success(`${module} module ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setModules(modules); // Revert
      toast.error('Failed to update modules');
    }
  };

  const handleDeactivate = async () => {
    const confirmed = await confirm({
      title: 'Deactivate Company',
      description: 'Are you sure you want to deactivate this company? All users associated with this company will lose access.',
      confirmText: 'Deactivate',
      variant: 'destructive',
    });
    if (!confirmed) return;

    try {
      await superAdminAPI.deactivateCompany(id);
      toast.success('Company deactivated');
      fetchCompany();
    } catch (error) {
      toast.error('Failed to deactivate company');
    }
  };

  const handleReactivate = async () => {
    const confirmed = await confirm({
      title: 'Reactivate Company',
      description: 'Are you sure you want to reactivate this company? All users associated with this company will regain access.',
      confirmText: 'Reactivate',
    });
    if (!confirmed) return;

    try {
      await superAdminAPI.reactivateCompany(id);
      toast.success('Company reactivated');
      fetchCompany();
    } catch (error) {
      toast.error('Failed to reactivate company');
    }
  };

  const handlePurge = async () => {
    if (purgePasscode !== '2545') {
      toast.error('Incorrect passcode');
      return;
    }

    setPurging(true);
    try {
      await superAdminAPI.purgeCompany(id);
      toast.success('Company and all data deleted permanently');
      navigate('/superadmin/companies');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge company data');
      setPurging(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await superAdminAPI.exportCompanyData(id);
      
      // If it's a blob, we need to check if it's an error (JSON) disguised as a blob
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.detail || 'Export failed');
      }

      const blob = new Blob([response.data], { type: 'application/x-zip-compressed' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${company?.name?.replace(/\s+/g, '_')}_backup.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match && match[1]) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Company data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      const msg = error.message || 'Failed to export company data';
      toast.error(msg);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/superadmin/companies')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{company?.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={company?.is_active ? 'default' : 'secondary'}>
                  {company?.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Created {new Date(company?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {company?.is_active ? (
            <Button variant="outline" className="text-destructive hover:text-destructive" onClick={handleDeactivate}>
              <ShieldAlert className="mr-2 h-4 w-4" />
              Deactivate Company
            </Button>
          ) : (
            <Button variant="outline" className="text-primary hover:text-primary" onClick={handleReactivate}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Activate Company
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} data-testid="save-company-btn">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold tracking-tight">Company Analytics</h2>
        <div className="flex items-center gap-4 bg-white p-2 rounded-lg border border-primary/10 shadow-sm">
          <Select value={selectedMonth.toString()} onValueChange={handleMonthChange}>
            <SelectTrigger className="w-[140px] border-none shadow-none focus:ring-0">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {[
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
              ].map((m, i) => (
                <SelectItem key={i + 1} value={(i + 1).toString()}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Separator orientation="vertical" className="h-6" />

          <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
            <SelectTrigger className="w-[100px] border-none shadow-none focus:ring-0">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                <SelectItem key={y} value={y.toString()}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-primary/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Employees</CardTitle>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Users className="h-4 w-4 text-blue-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics.total_employees}</div>
              <p className="text-xs text-muted-foreground mt-1">Full database count</p>
            </CardContent>
          </Card>

          <Card className="border-primary/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active Currently</CardTitle>
              <div className="p-2 bg-green-50 rounded-lg">
                <UserCheck className="h-4 w-4 text-green-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{analytics.active_employees}</div>
              <p className="text-xs text-muted-foreground mt-1">Onboarded as of today</p>
            </CardContent>
          </Card>

          <Card className="border-primary/10 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Monthly Inactive</CardTitle>
              <div className="p-2 bg-slate-50 rounded-lg">
                <UserMinus className="h-4 w-4 text-slate-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{analytics.inactive_employees}</div>
              <p className="text-xs text-muted-foreground mt-1">Exits / Not Active</p>
            </CardContent>
          </Card>

          <Card className="border-primary/10 shadow-sm ring-1 ring-purple-100">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Salary Payout</CardTitle>
              <div className="p-2 bg-purple-50 rounded-lg">
                <DollarSign className="h-4 w-4 text-purple-600" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">
                {formatIndianCurrency(analytics.total_salary_processed)}
              </div>
              <p className="text-xs font-medium text-purple-600/70 mt-1 flex items-center gap-1">
                For {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][selectedMonth - 1]} {selectedYear}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Company Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>Basic company details and contact information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="edit-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website || ''}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={formData.address || ''}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={formData.state || ''}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pincode</Label>
                  <Input
                    value={formData.pincode || ''}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="space-y-1">
                <CardTitle>System Users & Administrators</CardTitle>
                <CardDescription>Manage administrative access for this company</CardDescription>
              </div>
              <Button size="sm" onClick={handleAddUser}>
                <Plus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50/50">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                              <User className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">{u.first_name} {u.last_name}</div>
                              <div className="text-xs text-muted-foreground">{u.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-normal border-slate-300 bg-white">
                            {roles.find(r => r.id === u.role_id)?.name || u.role_id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.is_system ?
                            <Badge variant="secondary" className="bg-purple-50 text-purple-700 border-purple-100 hover:bg-purple-50">System User</Badge>
                            : <span className="text-xs text-slate-500 pl-2">Standard</span>
                          }
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditUser(u)}>
                              <Edit className="h-4 w-4 text-slate-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive"
                              onClick={() => handleDeleteUser(u)}
                              disabled={u.is_system}
                              title={u.is_system ? "System users cannot be deleted" : "Delete User"}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Statutory Information</CardTitle>
              <CardDescription>PF, ESI, TAN, and PAN details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PF Number</Label>
                  <Input
                    value={formData.pf_number || ''}
                    onChange={(e) => setFormData({ ...formData, pf_number: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ESI Number</Label>
                  <Input
                    value={formData.esi_number || ''}
                    onChange={(e) => setFormData({ ...formData, esi_number: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>TAN</Label>
                  <Input
                    value={formData.tan || ''}
                    onChange={(e) => setFormData({ ...formData, tan: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>PAN</Label>
                  <Input
                    value={formData.pan || ''}
                    onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>PT State</Label>
                <Input
                  value={formData.pt_state || ''}
                  onChange={(e) => setFormData({ ...formData, pt_state: e.target.value })}
                  placeholder="Professional Tax State"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>License Information</CardTitle>
              <CardDescription>License validity and auto-deactivation settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>License Start Date</Label>
                  <Input
                    type="date"
                    value={formData.license_start_date || ''}
                    onChange={(e) => setFormData({ ...formData, license_start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duration (Months)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.license_duration_months || 0}
                    onChange={(e) => setFormData({ ...formData, license_duration_months: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Expiry Note:</strong> The company and all its users will be automatically deactivated after the license period ends.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module Configuration */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Modules</CardTitle>
              <CardDescription>Enable or disable modules for this company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'employees', label: 'Employee Management' },
                { key: 'onboarding', label: 'Onboarding' },
                { key: 'leave', label: 'Leave Management' },
                { key: 'attendance', label: 'Attendance Management' },
                { key: 'payroll', label: 'Payroll Processing' },
                { key: 'salary_structure', label: 'Salary Structure' },
                { key: 'salary_advances', label: 'Salary Advances' },
                { key: 'performance', label: 'Performance Management' },
                { key: 'tax', label: 'Taxation & TDS' },
                { key: 'assets', label: 'IT Asset Management' },
                { key: 'feeds', label: 'Feeds & Announcements' },
                { key: 'org_chart', label: 'Organization Chart' },
                { key: 'stationery', label: 'Stationery' },
                { key: 'letters_reports', label: 'Letters & Reports' },
              ].map((module) => (
                <div key={module.key} className="flex items-center justify-between">
                  <Label htmlFor={module.key} className="cursor-pointer">
                    {module.label}
                  </Label>
                  <Switch
                    id={module.key}
                    checked={modules[module.key]}
                    onCheckedChange={(checked) => handleModuleToggle(module.key, checked)}
                    data-testid={`module-${module.key}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="mt-6 border-red-200 bg-red-50/30">
            <CardHeader>
              <CardTitle className="text-red-600 flex items-center gap-2 text-base">
                <AlertTriangle className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription className="text-red-600/70">Destructive actions for this company</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-red-600/80 mb-4 leading-relaxed">
                Deleting a company is an <strong>irreversible</strong> action. This will permanently delete
                every employee record, payroll data, tax filing, and configuration associated with this company.
              </p>
              <Button
                variant="destructive"
                className="w-full shadow-lg shadow-red-200"
                onClick={() => setShowPurgeDialog(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Purge All Company Data
              </Button>
            </CardContent>
          </Card>

          {/* Data Export Section */}
          <Card className="mt-6 border-primary/10 shadow-sm bg-slate-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Download className="h-5 w-5 text-primary" />
                Data Backup & Export
              </CardTitle>
              <CardDescription>
                Download a complete archive of this company's data in Excel format.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                This export includes all employee records, payroll history, attendance logs,
                and statutory filings. The generated ZIP file will contain multiple Excel sheets
                categorized by data type.
              </p>

              <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-slate-200">
                <Checkbox
                  id="export-consent"
                  checked={exportConsent}
                  onCheckedChange={setExportConsent}
                  className="mt-1"
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="export-consent"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    I understand that I am responsible for the safety of this data.
                  </label>
                  <p className="text-xs text-muted-foreground">
                    This data contains sensitive employee information. PagaHR ensures that
                    this data is fetched securely and will not be misused or shared with
                    any third parties.
                  </p>
                </div>
              </div>

              <Button
                onClick={handleExportData}
                disabled={exporting || !exportConsent}
                className="w-full mt-2"
                variant="outline"
              >
                {exporting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Backup...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Complete Archive
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
      <ConfirmationDialog {...dialogProps} />

      {/* Purge Confirmation Dialog */}
      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Critical: Confirm Permanent Deletion
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. Please enter the master passcode to authorize the complete removal of <strong>{company?.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="passcode" className="flex items-center gap-2">
                <Key className="h-4 w-4" />
                Master Passcode
              </Label>
              <Input
                id="passcode"
                type="password"
                placeholder="Enter 4-digit passcode"
                className="text-center text-2xl tracking-[1em] font-mono"
                value={purgePasscode}
                onChange={(e) => setPurgePasscode(e.target.value)}
                maxLength={4}
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handlePurge()}
              />
            </div>
            <div className="p-3 bg-red-100/50 rounded-lg border border-red-200 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700 leading-tight">
                Warning: This will execute a cascading delete across all database collections.
                Data recovery will not be possible.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurgeDialog(false)} disabled={purging}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handlePurge}
              disabled={purging || purgePasscode.length < 4}
              className="gap-2"
            >
              {purging ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Purging...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" /> Hard Delete Everything
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit System User' : 'Create System User'}</DialogTitle>
            <DialogDescription>{editingUser ? 'Update user details' : `Add an administrator or system user for ${company?.name}`}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input value={userForm.first_name} onChange={e => setUserForm({ ...userForm, first_name: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input value={userForm.last_name} onChange={e => setUserForm({ ...userForm, last_name: e.target.value })} required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{editingUser ? 'New Password (Optional)' : 'Password'}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 font-normal hover:bg-transparent"
                  onClick={generatePassword}
                >
                  <RefreshCw className="mr-1 h-3 w-3" /> Generate Random
                </Button>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={userForm.password}
                  onChange={e => setUserForm({ ...userForm, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave blank to keep current' : ''}
                  className="pr-10 font-mono"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4 text-slate-400" /> : <Eye className="h-4 w-4 text-slate-400" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={userForm.role_id} onValueChange={v => setUserForm({ ...userForm, role_id: v })} required>
                <SelectTrigger><SelectValue placeholder="Select Role" /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2 pt-2 bg-slate-50 p-3 rounded-md border border-slate-200">
              <Checkbox id="is_system" checked={userForm.is_system} onCheckedChange={c => setUserForm({ ...userForm, is_system: c })} />
              <Label htmlFor="is_system" className="font-normal cursor-pointer flex-1">Mark as System User</Label>
              <Badge variant="secondary" className="text-[10px]">Protected</Badge>
            </div>
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsUserDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creatingUser}>
                {creatingUser && <Loader2 className="h-4 w-4 animate-spin mr-2" />} {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
